/**
 * Global Notification Service
 *
 * Provides a centralized way to display toast notifications throughout the application.
 * Built on top of Sonner library for beautiful, accessible toast notifications.
 *
 * @module notificationService
 *
 * @example
 * ```tsx
 * import { notificationService } from '@/services/notificationService';
 *
 * // Show success message
 * notificationService.success('Changes saved successfully');
 *
 * // Show error with description
 * notificationService.error('Failed to save', 'Please check your connection and try again');
 *
 * // Show with action button
 * notificationService.info('New update available', 'Click to refresh', {
 *   action: {
 *     label: 'Refresh',
 *     onClick: () => window.location.reload()
 *   }
 * });
 * ```
 */

import { toast } from "sonner";

/**
 * Notification types
 */
export type NotificationType = "success" | "error" | "info" | "warning" | "loading" | "default";

/**
 * Notification action interface
 */
export interface NotificationAction {
  label: string;
  onClick: () => void;
}

/**
 * Notification options interface
 */
export interface NotificationOptions {
  /** Duration in milliseconds (default: 4000) */
  duration?: number;
  /** Position of the toast */
  position?:
    | "top-left"
    | "top-center"
    | "top-right"
    | "bottom-left"
    | "bottom-center"
    | "bottom-right";
  /** Action button configuration */
  action?: NotificationAction;
  /** Cancel button configuration */
  cancel?: NotificationAction;
  /** Custom icon */
  icon?: React.ReactNode;
  /** Unique ID for the notification */
  id?: string | number;
  /** Dismiss notification on click */
  dismissible?: boolean;
  /** Description text (secondary message) */
  description?: string;
}

/**
 * NotificationService class
 * Provides methods to display various types of notifications
 */
class NotificationService {
  /**
   * Show a success notification
   * @param message - Primary message to display
   * @param description - Optional secondary message
   * @param options - Additional notification options
   * @returns Toast ID
   */
  success(message: string, description?: string, options?: NotificationOptions): string | number {
    return toast.success(message, {
      description,
      duration: options?.duration || 4000,
      position: options?.position,
      action: options?.action,
      cancel: options?.cancel,
      icon: options?.icon,
      id: options?.id,
      dismissible: options?.dismissible !== false,
    });
  }

  /**
   * Show an error notification
   * @param message - Primary error message to display
   * @param description - Optional error description
   * @param options - Additional notification options
   * @returns Toast ID
   */
  error(message: string, description?: string, options?: NotificationOptions): string | number {
    return toast.error(message, {
      description,
      duration: options?.duration || 5000, // Errors stay a bit longer
      position: options?.position,
      action: options?.action,
      cancel: options?.cancel,
      icon: options?.icon,
      id: options?.id,
      dismissible: options?.dismissible !== false,
    });
  }

  /**
   * Show an info notification
   * @param message - Primary message to display
   * @param description - Optional secondary message
   * @param options - Additional notification options
   * @returns Toast ID
   */
  info(message: string, description?: string, options?: NotificationOptions): string | number {
    return toast.info(message, {
      description,
      duration: options?.duration || 4000,
      position: options?.position,
      action: options?.action,
      cancel: options?.cancel,
      icon: options?.icon,
      id: options?.id,
      dismissible: options?.dismissible !== false,
    });
  }

  /**
   * Show a warning notification
   * @param message - Primary warning message to display
   * @param description - Optional warning description
   * @param options - Additional notification options
   * @returns Toast ID
   */
  warning(message: string, description?: string, options?: NotificationOptions): string | number {
    return toast.warning(message, {
      description,
      duration: options?.duration || 4500,
      position: options?.position,
      action: options?.action,
      cancel: options?.cancel,
      icon: options?.icon,
      id: options?.id,
      dismissible: options?.dismissible !== false,
    });
  }

  /**
   * Show a loading notification
   * Useful for long-running operations
   * @param message - Loading message to display
   * @param description - Optional loading description
   * @param options - Additional notification options
   * @returns Toast ID
   */
  loading(message: string, description?: string, options?: NotificationOptions): string | number {
    return toast.loading(message, {
      description,
      duration: options?.duration || Infinity, // Loading toasts don't auto-dismiss
      position: options?.position,
      icon: options?.icon,
      id: options?.id,
      dismissible: options?.dismissible !== false,
    });
  }

  /**
   * Show a default notification (no specific type)
   * @param message - Message to display
   * @param description - Optional description
   * @param options - Additional notification options
   * @returns Toast ID
   */
  default(message: string, description?: string, options?: NotificationOptions): string | number {
    return toast(message, {
      description,
      duration: options?.duration || 4000,
      position: options?.position,
      action: options?.action,
      cancel: options?.cancel,
      icon: options?.icon,
      id: options?.id,
      dismissible: options?.dismissible !== false,
    });
  }

  /**
   * Show a promise-based notification
   * Automatically shows loading, success, or error states based on promise resolution
   * @param promise - Promise to track
   * @param messages - Messages for each state
   * @returns Toast ID
   */
  promise<T>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: any) => string);
    }
  ): string | number {
    return toast.promise(promise, messages) as string | number;
  }

  /**
   * Dismiss a specific notification by ID
   * @param toastId - ID of the toast to dismiss
   */
  dismiss(toastId?: string | number): void {
    toast.dismiss(toastId);
  }

  /**
   * Dismiss all active notifications
   */
  dismissAll(): void {
    toast.dismiss();
  }

  /**
   * Custom notification for API responses
   * Handles common API response patterns
   * @param success - Whether the operation was successful
   * @param message - Message to display
   * @param description - Optional description
   */
  apiResponse(success: boolean, message: string, description?: string): string | number {
    if (success) {
      return this.success(message, description);
    } else {
      return this.error(message, description);
    }
  }

  /**
   * Show a notification for data operations (Create, Update, Delete)
   * @param operation - Type of operation performed
   * @param entity - Entity type (e.g., 'user', 'incident', 'problem')
   * @param success - Whether the operation was successful
   * @param customMessage - Optional custom message to override default
   */
  dataOperation(
    operation: "create" | "update" | "delete",
    entity: string,
    success: boolean,
    customMessage?: string
  ): string | number {
    const operationLabels = {
      create: "created",
      update: "updated",
      delete: "deleted",
    };

    if (success) {
      const message =
        customMessage ||
        `${entity.charAt(0).toUpperCase() + entity.slice(1)} ${operationLabels[operation]} successfully`;
      return this.success(message);
    } else {
      const message = customMessage || `Failed to ${operation} ${entity}`;
      return this.error(message, "Please try again or contact support if the problem persists");
    }
  }

  /**
   * Show a notification for network errors
   * @param error - Error object
   * @param customMessage - Optional custom message
   */
  networkError(error?: any, customMessage?: string): string | number {
    const message = customMessage || "Network error occurred";
    const description = error?.message || "Please check your internet connection and try again";
    return this.error(message, description);
  }

  /**
   * Show a notification for validation errors
   * @param message - Validation error message
   * @param fields - Optional list of invalid fields
   */
  validationError(message: string, fields?: string[]): string | number {
    const description = fields?.length
      ? `Invalid fields: ${fields.join(", ")}`
      : "Please check your input and try again";
    return this.warning(message, description);
  }

  /**
   * Show a notification for permission errors
   * @param action - Action that was attempted
   */
  permissionError(action?: string): string | number {
    const message = action ? `You don't have permission to ${action}` : "Permission denied";
    return this.error(message, "Please contact your administrator if you need access");
  }
}

/**
 * Singleton instance of NotificationService
 * Export this to use throughout the application
 */
export const notificationService = new NotificationService();

/**
 * Default export for convenience
 */
export default notificationService;
